// 최소 HTML 안전 처리 — <strong>/<code>/<br>/<em>만 React 요소로 복원, 나머지는 텍스트
import { createElement, type ReactNode } from 'react';

type Frag = string | { tag: 'strong' | 'code' | 'em'; children: Frag[] } | { tag: 'br' };

const ALLOWED = new Set(['strong', 'code', 'em', 'br']);

function parse(html: string): Frag[] {
  const out: Frag[] = [];
  let i = 0;
  const stack: Array<{ tag: 'strong' | 'code' | 'em'; children: Frag[] }> = [];

  const push = (frag: Frag) => {
    const target = stack.length ? stack[stack.length - 1]!.children : out;
    target.push(frag);
  };

  while (i < html.length) {
    if (html[i] === '<') {
      const closeMatch = html.slice(i).match(/^<\/([a-z]+)\s*>/i);
      if (closeMatch) {
        const tag = closeMatch[1]!.toLowerCase();
        if (stack.length && stack[stack.length - 1]!.tag === tag) {
          const popped = stack.pop()!;
          if (stack.length) stack[stack.length - 1]!.children.push(popped);
          else out.push(popped);
        }
        i += closeMatch[0].length;
        continue;
      }
      const openMatch = html.slice(i).match(/^<([a-z]+)([^>]*)>/i);
      if (openMatch) {
        const tag = openMatch[1]!.toLowerCase();
        i += openMatch[0].length;
        if (tag === 'br') {
          push({ tag: 'br' });
        } else if (ALLOWED.has(tag) && (tag === 'strong' || tag === 'code' || tag === 'em')) {
          stack.push({ tag, children: [] });
        }
        continue;
      }
    }
    const nextTag = html.indexOf('<', i);
    const end = nextTag === -1 ? html.length : nextTag;
    const text = html.slice(i, end);
    if (text) push(decodeEntities(text));
    i = end;
  }
  while (stack.length) {
    const popped = stack.pop()!;
    if (stack.length) stack[stack.length - 1]!.children.push(popped);
    else out.push(popped);
  }
  return out;
}

function decodeEntities(s: string): string {
  return s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

let keyCounter = 0;
function nextKey() { return `s${++keyCounter}`; }

function toReact(frag: Frag): ReactNode {
  if (typeof frag === 'string') return frag;
  if ((frag as { tag: string }).tag === 'br') {
    return createElement('br', { key: nextKey() });
  }
  const { tag, children } = frag as { tag: 'strong' | 'code' | 'em'; children: Frag[] };
  return createElement(tag, { key: nextKey() }, ...children.map(toReact));
}

export function renderSafeHtml(html: string): ReactNode[] {
  return parse(html).map(toReact);
}

export function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '');
}
